import { Minus, Plus } from "lucide-react";
import { type JSX, type ReactNode } from "react";
import { cn } from "./lib/utils";
import { AppIcon } from "./AppIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export interface AppEntryProps {
  id: string;
  label: string;
  /** Image URL or data URL from the app's registry entry. */
  icon: string;
  href?: string;
  route?: string;
  active: boolean;
  collapsed: boolean;
  /** True for workspace-mockup placeholder apps that have no real route. */
  disabled?: boolean;
  /** True when the app has nav sections to show beneath it; drives the +/- indicator. */
  expandable?: boolean;
  /** True while this app's nav sections are open beneath it. */
  expanded?: boolean;
  onExpand: (appId: string) => void;
}

/** One app's row in the sidebar: the app's registry icon plus its wordmark, in the shell's base colours. */
export function AppEntry({
  id,
  label,
  icon,
  href,
  route,
  active,
  collapsed,
  disabled = false,
  expandable = false,
  expanded = false,
  onExpand
}: AppEntryProps): JSX.Element {
  const rowClassName = cn(
    "flex w-full items-center rounded-card py-2 no-underline transition-[background-color,color,transform] duration-150 ease-out active:scale-[.99] motion-reduce:transition-none motion-reduce:active:scale-100",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-shell-canvas",
    collapsed ? "justify-center px-0" : "gap-1 px-3",
    disabled ? "cursor-not-allowed opacity-60" : "hover:bg-sidebar-surface-hover"
  );

  const content: ReactNode = (
    <>
      <AppIcon icon={icon} />
      {!collapsed && (
        <span
          className={cn(
            // font-display is Druk with the previous display font as fallback: a
            // display face that only reads right in caps, so it is always uppercase.
            // text-box trims the line box to the capitals; these faces reserve extra
            // space below the baseline, which otherwise leaves the label sitting high
            // next to the centred icon tile.
            "truncate font-display text-xl leading-8 uppercase text-sidebar-text [text-box:trim-both_cap_alphabetic]"
          )}
        >
          {label}
        </span>
      )}
      {!collapsed && expandable && !disabled && (
        <span aria-hidden="true" data-platform-app-toggle="" className="ml-auto flex shrink-0 items-center text-sidebar-text">
          {expanded ? <Minus className="size-4" strokeWidth={1.75} /> : <Plus className="size-4" strokeWidth={1.75} />}
        </span>
      )}
    </>
  );

  const link =
    disabled || !href || !route ? (
      <span
        data-platform-app-switcher-link=""
        data-switcher-app-id={id}
        aria-disabled="true"
        className={rowClassName}
      >
        {content}
      </span>
    ) : (
      <a
        href={href}
        data-platform-app-switcher-link=""
        data-switcher-app-id={id}
        aria-current={active ? "true" : undefined}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
            return;
          }

          event.preventDefault();
          onExpand(id);
        }}
        className={rowClassName}
      >
        {content}
      </a>
    );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
